<template>
	<PageWrapper>
		<HeroesServiceArea
			:image="heroImage"
			:text="heroRichText"
			:area-name="areaName"
			:tracking-location="trackingLocations.hero"
		/>
		<PageDivider class="mt-4 pb-12 sm:mt-0 sm:pb-0 px-12" />
		<ServiceAreasCta
			v-if="areaName"
			:area-name="areaName"
			:area-city="city"
		/>
		<PageDivider class="mt-4 pb-12 sm:mt-0 sm:pb-0 px-12" />
		<LazyVisibleBusinessFiberCTA
			:copy="businessFiberCTACopy"
			hydrate-on-visible
		/>
		<LazyVisibleTestimonials
			:tracking-location="trackingLocations.testimonials"
			hydrate-on-visible
		/>
		<LazyVisibleServiceAreasCarousel
			hydrate-on-visible
			class="contain-layout"
		/>
		<LazyVisibleFaqs
			hydrate-on-visible
			:faqs="faqs"
			:tracking-location="trackingLocations.faq"
			divider="both"
		/>
		<LazyVisibleBlogCarousel
			:id="`service-area-${areaName}`"
			:tracking-location="trackingLocations.blogCarousel"
			hydrate-on-visible
		/>
		<LazyVisibleBannersAddressChecker
			:tracking-location="trackingLocations.ctaBanner"
			hydrate-on-visible
		/>
	</PageWrapper>
</template>

<script setup lang="ts">
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { SERVICE_AREA_PAGE_LOCATIONS_MAP } from '@/constants/ga4/tracking-ids/pages/service-area'
import {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE
} from '@/constants/index.js'
import {
	JSON_LD_AGGREGATE_RATING,
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LATITUDE,
	JSON_LD_LOGO, JSON_LD_LONGITUDE,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_AREA_SERVED,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_REVIEWS,
	JSON_LD_ORGANIZATION_SAME_AS,
	JSON_LD_PRICE_RANGE
} from '@/constants/json-ld'
import type { SvgImageProps } from '@/types/components/svg-image'
import type {
	ServiceAreaPageLocations, ServiceAreaPageLocationsMap
} from '@/types/ga4/tracking-locations'
import type { HeroMarkdown } from '@/types/storyblok/hero'
import { mapFaqsForJsonLd } from '@/utils/mapFaqsForJsonLd'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { t } = useI18n({
	useScope: 'global'
})

const route = useRoute()

const { getStory } = useStoryblokWithErrorReporting()

const storyblok = await getStory(`service-areas/${route?.params?.area}`)

const { story } = storyblok.value?.data || {}

const {
	title, description
} = story?.content?.metatags ?? {
	title: '',
	description: ''
}

const heroImage = computed<SvgImageProps['image']>(() => {
	const img = story?.content?.cityCardContent?.[0]?.image
	return img
		? {
			id: img.id,
			url: img.filename,
			alt: img.alt
		}
		: {
			url: '',
			alt: ''
		}
})

const heroContent = story?.content?.heroContent?.[0]
const heroRichText = computed<HeroMarkdown>(() => {
	return heroContent
		? {
			title: heroContent.title,
			subtitle: heroContent?.subtitle || undefined,
			description: heroContent?.description || undefined
		}
		: {
			title: {
				type: 'doc',
				content: []
			}
		}
})

const areaName = computed<string | undefined>(() => story?.name)

const businessFiberCTACopy = computed(() => ({
	id: `business-fiber-cta-${areaName.value}`,
	content: {
		leading: t('business-fiber-cta.leading', { areaName: areaName.value }),
		title: t('business-fiber-cta.title'),
		description: t('business-fiber-cta.description', { areaName: areaName.value }),
		description2: t('business-fiber-cta.description2'),
		description3: t('business-fiber-cta.description3', { areaName: areaName.value?.split(',')[0] })
	},
	cta: {
		label: t('business-fiber-cta.cta.label'),
		to: t('business-fiber-cta.cta.to')
	}
}))

const trackingLocations = computed<ServiceAreaPageLocations>(() =>
	SERVICE_AREA_PAGE_LOCATIONS_MAP[areaName.value as keyof ServiceAreaPageLocationsMap]
	?? SERVICE_AREA_PAGE_LOCATIONS_MAP.unknown
)

const faqs = computed(() => {
	return story?.content?.faqContent
})

const cityCardContent = computed(() => {
	return story?.content?.cityCardContent?.[0]
})

const cityImage = computed(() => {
	return cityCardContent.value?.image.filename
})

const city = computed(() => {
	return areaName.value?.split(',')[0] || ''
})

const jsonLdFaqs = computed(() => {
	return mapFaqsForJsonLd(faqs.value)
})

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => cityImage.value,
	ogUrl: () => `${BASE_URL}${route?.fullPath}`,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => cityImage.value,
	twitterCard: () => 'summary_large_image'
})

useHead({
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'LocalBusiness',
			'name': COMPANY_NAME,
			'url': BASE_URL,
			'logo': JSON_LD_LOGO,
			'image': JSON_LD_LOGO,
			'telephone': COMPANY_PHONE,
			'description': t('meta-description', { COMPANY_NAME }),
			'contactPoint': JSON_LD_ORGANIZATION_CONTACT_POINT,
			'sameAs': JSON_LD_ORGANIZATION_SAME_AS,
			'address': JSON_LD_ORGANIZATION_ADDRESS,
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED,
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG,
			'review': JSON_LD_ORGANIZATION_REVIEWS,
			'aggregateRating': JSON_LD_AGGREGATE_RATING,
			'priceRange': JSON_LD_PRICE_RANGE,
			'geo': {
				'@type': 'GeoCoordinates',
				'latitude': JSON_LD_LATITUDE,
				'longitude': JSON_LD_LONGITUDE
			}
		},
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'Service Areas',
					'item': `${BASE_URL}/service-areas`
				}, {
					'@type': 'ListItem',
					'position': 3,
					'name': city.value,
					'item': `${BASE_URL}${route?.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'provider': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'telephone': COMPANY_PHONE
			},
			'serviceType': 'Fiber-Optic Internet Access',
			'serviceOutput': 'Internet service',
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED,
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
		},
		{
			'@type': 'FAQPage',
			'mainEntity': jsonLdFaqs.value
		}
	]
})
</script>
